// from https://github.com/nuxt/image/blob/main/src/runtime/providers/ipx.ts
import { joinURL, encodePath, encodeParam } from 'ufo';
import { createOperationsGenerator, defineProvider } from '@nuxt/image/runtime';
import type { ImageModifiers } from '@nuxt/image';
import type { HTTPStorageOptions, NodeFSSOptions, IPXOptions as CoreIPXOptions } from 'ipx';
import type { WatermarkPosition, WatermarkSizeShort } from '@i22team/td-longtail-vue-components';

export interface EGSIPXRuntimeConfig extends Omit<CoreIPXOptions, 'storage' | 'httpStorage'> {
  http: HTTPStorageOptions;
  fs: NodeFSSOptions;
  baseURL: string;
}

// Reference: https://github.com/unjs/ipx?tab=readme-ov-file#modifiers
// TODO: https://github.com/unjs/ipx/issues/199
export interface EGSIPXModifiers extends Omit<ImageModifiers, 'fit' | 'format' | 'blur'> {
  format:
    'jpeg' | 'jpg' | 'png' | 'webp' | 'avif' | 'gif' | 'heif' | 'tiff' | 'auto' | (string & {});
  fit: 'contain' | 'cover' | 'fill' | 'inside' | 'outside' | (string & {});
  resize: string;
  quality: number | string;
  background: string;
  position: string;
  enlarge: true | 'true';
  kernel: 'nearest' | 'cubic' | 'mitchell' | 'lanczos2' | 'lanczos3' | (string & {});
  trim: number | string;
  extend: string;
  extract: string;
  crop: string;
  rotate: number | string;
  flip: true | 'true';
  flop: true | 'true';
  sharpen: number | string;
  median: number | string;
  blur: number | string;
  flatten: true | 'true';
  gamma: string;
  negate: true | 'true';
  normalize: true | 'true';
  threshold: number | string;
  modulate: string;
  tint: number | string;
  grayscale: true | 'true';
  animated: true | 'true';
  // Custom modifier (not a native ipx one, see server/routes/_ipx/[...].ts): places a watermark in a corner.
  watermark: WatermarkPosition;
  watermarkSize: WatermarkSizeShort;
}

export interface IPXOptions extends Omit<EGSIPXRuntimeConfig, 'alias'> {
  modifiers: Partial<EGSIPXModifiers>;
}

export const operationsGenerator = createOperationsGenerator({
  keyMap: {
    format: 'f',
    width: 'w',
    height: 'h',
    resize: 's',
    quality: 'q',
    background: 'b',
    position: 'pos',
    watermark: 'i',
    watermarkSize: 'is',
  },
  formatter: (key, val: string | number | boolean) =>
    encodeParam(key) + '_' + encodeParam(val.toString()),
});

export default defineProvider<Partial<IPXOptions>>({
  validateDomains: true,
  supportsAlias: true,
  getImage: (src, { modifiers, baseURL }, ctx) => {
    if (modifiers.width && modifiers.height) {
      modifiers.resize = `${modifiers.width}x${modifiers.height}`;
      delete modifiers.width;
      delete modifiers.height;
    }

    const params = operationsGenerator(modifiers) || '_';

    if (!baseURL) {
      baseURL = joinURL(ctx.options.nuxt.baseURL, '/_ipx');
    }

    return {
      url: joinURL(baseURL, params, encodePath(src)),
    };
  },
});
